import { CommonModule } from '@angular/common';
import { Component, computed, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { SessionService } from '../../services/session.service';
import type { MlActiveListingQuality, MlListingIssue, MlListingsQualityOverview } from '../../models/api.models';

type IssueFilter = 'all' | 'bad_chars' | 'description_empty' | 'description_short' | 'few_pictures' | 'few_attributes';
type ListingRevisionDraft = {
  listing: MlActiveListingQuality;
  currentTitle: string;
  currentDescription: string;
  newTitle: string;
  newDescription: string;
  saving: boolean;
  error: string | null;
  titleLocked: boolean;
};

@Component({
  selector: 'app-listings-dashboard-page',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './listings-dashboard-page.component.html',
  styleUrl: './listings-dashboard-page.component.css'
})
export class ListingsDashboardPageComponent {
  readonly loading = signal(false);
  readonly syncing = signal(false);
  readonly error = signal<string | null>(null);
  readonly message = signal<string | null>(null);
  readonly data = signal<MlListingsQualityOverview | null>(null);
  readonly onlyProblems = signal(true);
  readonly issueFilter = signal<IssueFilter>('all');
  readonly search = signal('');
  readonly syncProgress = signal<{ processed: number; total: number } | null>(null);
  readonly copiedItemId = signal<string | null>(null);
  readonly revisionDraft = signal<ListingRevisionDraft | null>(null);
  readonly exportUrl = this.api.getMlListingsExportUrl();

  readonly listings = computed(() => this.data()?.listings ?? []);
  readonly totals = computed(() => this.data()?.totals ?? null);
  readonly busy = computed(() => this.loading() || this.syncing());
  readonly revisionTitleCount = computed(() => this.revisionDraft()?.newTitle.length ?? 0);
  readonly revisionCanSave = computed(() => {
    const draft = this.revisionDraft();
    if (!draft || draft.saving) return false;
    const title = this.normalizeTitle(draft.newTitle);
    const description = this.normalizeDescription(draft.newDescription);
    const titleChanged = !draft.titleLocked && title !== this.normalizeTitle(draft.currentTitle);
    const descriptionChanged = description !== this.normalizeDescription(draft.currentDescription);
    const titleValid = !titleChanged || (title.length >= 10 && title.length <= 60);
    const descriptionValid = !descriptionChanged || description.length > 0;
    return titleValid && descriptionValid && (titleChanged || descriptionChanged);
  });
  readonly reviewCountLabel = computed(() => {
    const totals = this.totals();
    if (!totals) return 'Nenhum anúncio carregado ainda';
    if (totals.total === 0) return 'Clique em atualizar para buscar anúncios ativos';
    return `${totals.withIssues} de ${totals.total} anúncios merecem revisão`;
  });

  constructor(
    private readonly api: ApiService,
    private readonly session: SessionService
  ) {
    void this.bootstrap();
  }

  async bootstrap(): Promise<void> {
    const ok = await this.session.refresh();
    if (!ok) {
      this.error.set('Acesso não autenticado. Entre novamente pelo Cloudflare Access.');
      return;
    }
    await this.loadListings();
  }

  async loadListings(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    this.message.set(null);
    try {
      const issue = this.issueParam();
      const response = await this.api.getMlListingsQuality({
        limit: 160,
        offset: 0,
        onlyProblems: this.onlyProblems(),
        issue,
        search: this.search().trim() || null
      });
      this.data.set(response);
      this.message.set(
        response.totals.lastSyncedAt
          ? `Dados carregados do banco. Última varredura: ${this.formatDateTime(response.totals.lastSyncedAt)}.`
          : 'Ainda não há uma varredura salva. Clique em “Atualizar anúncios ativos”.'
      );
    } catch (error: any) {
      this.error.set(error?.error?.error ?? 'Falha ao carregar anúncios salvos.');
      this.data.set(null);
    } finally {
      this.loading.set(false);
    }
  }

  async syncActiveListings(): Promise<void> {
    this.syncing.set(true);
    this.error.set(null);
    this.message.set('Buscando anúncios ativos no Mercado Livre...');
    this.syncProgress.set({ processed: 0, total: 0 });
    try {
      let offset = 0;
      let total = 0;
      const limit = 20;
      do {
        const step = await this.api.syncMlListingsStep(offset, limit);
        total = step.total;
        offset = step.nextOffset ?? step.total;
        this.syncProgress.set({ processed: Math.min(offset, total), total });
        this.message.set(`Analisando anúncios ativos: ${Math.min(offset, total)} de ${total}.`);
        if (step.done || step.nextOffset === null) break;
      } while (offset < total);
      this.message.set('Varredura concluída. Agora a lista mostra os anúncios que mais precisam de revisão.');
      await this.loadListings();
    } catch (error: any) {
      this.error.set(error?.error?.error ?? 'Falha ao atualizar anúncios ativos pela API Mercado Livre.');
    } finally {
      this.syncing.set(false);
      this.syncProgress.set(null);
    }
  }

  setIssueFilter(value: IssueFilter): void {
    this.issueFilter.set(value);
    void this.loadListings();
  }

  setOnlyProblems(value: boolean): void {
    this.onlyProblems.set(value);
    void this.loadListings();
  }

  setSearch(value: string): void {
    this.search.set(value);
  }

  async applySearch(): Promise<void> {
    await this.loadListings();
  }

  logout(): void {
    window.location.href = '/cdn-cgi/access/logout?returnTo=' + encodeURIComponent(window.location.origin);
  }

  qualityLabel(score: number): string {
    if (score >= 85) return 'Bom';
    if (score >= 65) return 'Pode melhorar';
    if (score >= 40) return 'Atenção';
    return 'Crítico';
  }

  qualityClass(score: number): string {
    if (score >= 85) return 'ok';
    if (score >= 65) return 'warning';
    return 'danger';
  }

  issueClass(issue: MlListingIssue): string {
    return issue.severity;
  }

  primaryAction(listing: MlActiveListingQuality): string {
    if (listing.issues.some((issue) => issue.code.includes('bad_chars'))) return 'Corrigir caracteres quebrados';
    if (listing.issues.some((issue) => issue.code.includes('leetspeak') || issue.code.includes('excessive_symbols'))) return 'Limpar escrita e remover símbolos';
    if (listing.issues.some((issue) => issue.code === 'description_glued_fields')) return 'Separar informações grudadas';
    if (listing.issues.some((issue) => issue.code === 'description_unstructured')) return 'Organizar descrição por tópicos';
    if (listing.issues.some((issue) => issue.code === 'description_empty')) return 'Criar descrição completa';
    if (listing.issues.some((issue) => issue.code === 'description_short')) return 'Enriquecer descrição';
    if (listing.issues.some((issue) => issue.code === 'few_pictures')) return 'Adicionar fotos reais';
    if (listing.issues.some((issue) => issue.code === 'few_attributes')) return 'Completar ficha técnica';
    return 'Revisar para melhorar conversão';
  }

  descriptionPreview(listing: MlActiveListingQuality): string {
    const text = (listing.descriptionPlainText ?? '').replace(/\s+/g, ' ').trim();
    if (!text) return 'Sem descrição cadastrada.';
    return text.length > 280 ? `${text.slice(0, 280)}...` : text;
  }

  buildAiPrompt(listing: MlActiveListingQuality): string {
    const issues = listing.issues.map((issue) => `- ${issue.label}: ${issue.detail}`).join('\n') || '- Sem problema automático, mas revise oportunidade de melhoria.';
    const description = (listing.descriptionPlainText ?? '').trim() || 'Sem descrição cadastrada.';
    return `Você é especialista em Mercado Livre para autopeças. Melhore este anúncio sem inventar informação técnica que não esteja no texto.

Boas práticas obrigatórias:
- Título claro, sem símbolos, sem !!!, sem letras separadas e sem escrever OR1G1NAL/S-€-M-1-N-0-V-0.
- Use marca + peça + aplicação/modelo + motor/ano somente quando estiver explícito.
- Use "para" quando indicar compatibilidade. Exemplo: Coletor de Admissão para Polo Golf Fox 1.6 8v.
- Não coloque condição como usado/seminovo no título. Coloque na descrição.
- Descrição em blocos: Produto, Aplicação, Código da peça, Estado da peça, Observações importantes.
- Corrija acentos, texto grudado e caracteres quebrados.
- Não prometa originalidade, garantia, compatibilidade ou estado se isso não estiver informado.
- Se faltar dado técnico, liste o que a Luscilia precisa confirmar.

Item Mercado Livre: ${listing.itemId}
Título atual: ${listing.title ?? 'Sem título'}
Preço atual: R$ ${listing.price.toFixed(2)}
Fotos: ${listing.picturesCount}
Atributos preenchidos: ${listing.attributesCount}
Problemas detectados:
${issues}

Descrição atual:
${description}

Entregue em português do Brasil:
1. Título melhorado com até 60 caracteres.
2. Descrição melhorada para Mercado Livre.
3. Lista de informações que faltam confirmar com a Luscilia.
4. Sugestões de fotos/ficha técnica para melhorar o anúncio.`;
  }

  async copyAiPrompt(listing: MlActiveListingQuality): Promise<void> {
    const prompt = this.buildAiPrompt(listing);
    try {
      await navigator.clipboard.writeText(prompt);
      this.copiedItemId.set(listing.itemId);
      setTimeout(() => this.copiedItemId.set(null), 2200);
    } catch {
      this.error.set('Não consegui copiar automaticamente. Selecione o texto do anúncio e copie manualmente.');
    }
  }

  openRevision(listing: MlActiveListingQuality): void {
    const currentTitle = listing.title ?? '';
    const currentDescription = listing.descriptionPlainText ?? '';
    this.revisionDraft.set({
      listing,
      currentTitle,
      currentDescription,
      newTitle: currentTitle,
      newDescription: currentDescription,
      saving: false,
      error: null,
      titleLocked: false
    });
  }

  closeRevision(): void {
    const draft = this.revisionDraft();
    if (draft?.saving) return;
    this.revisionDraft.set(null);
  }

  setRevisionTitle(value: string): void {
    this.revisionDraft.update((draft) => (draft && !draft.titleLocked ? { ...draft, newTitle: value, error: null } : draft));
  }

  setRevisionDescription(value: string): void {
    this.revisionDraft.update((draft) => (draft ? { ...draft, newDescription: value, error: null } : draft));
  }

  async applyRevision(): Promise<void> {
    const draft = this.revisionDraft();
    if (!draft || !this.revisionCanSave()) return;

    this.revisionDraft.update((current) => (current ? { ...current, saving: true, error: null } : current));
    try {
      const response = await this.api.applyMlListingRevision(draft.listing.itemId, {
        confirmItemId: draft.listing.itemId,
        currentTitle: this.normalizeTitle(draft.currentTitle),
        currentDescription: this.normalizeDescription(draft.currentDescription),
        newTitle: this.normalizeTitle(draft.newTitle),
        newDescription: this.normalizeDescription(draft.newDescription)
      });
      const scoreChange = `${response.before.qualityScore}/100 -> ${response.after.qualityScore}/100`;
      const warning = response.warnings?.[0] ? ` Aviso: ${response.warnings[0]}` : '';
      const changedLabels = response.changedFields.map((field) => (field === 'title' ? 'título' : 'descrição')).join(', ') || 'nenhum';
      this.revisionDraft.set(null);
      await this.loadListings();
      this.message.set(`Revisão aplicada em ${response.itemId}. Campos: ${changedLabels}. Nota: ${scoreChange}.${warning}`);
    } catch (error: any) {
      const code = error?.error?.code;
      const message = error?.error?.error ?? 'Falha ao aplicar revisão pela API do Mercado Livre.';
      this.revisionDraft.update((current) =>
        current
          ? {
              ...current,
              saving: false,
              titleLocked: code === 'TITLE_LOCKED_BY_FAMILY_NAME' ? true : current.titleLocked,
              newTitle: code === 'TITLE_LOCKED_BY_FAMILY_NAME' ? current.currentTitle : current.newTitle,
              error: message
            }
          : current
      );
    }
  }

  improvementTips(listing: MlActiveListingQuality): string[] {
    const codes = new Set(listing.issues.map((issue) => issue.code));
    const tips: string[] = [];
    if (codes.has('title_bad_chars') || codes.has('description_bad_chars')) tips.push('Corrigir acentos, caracteres quebrados e entidades HTML visíveis.');
    if (codes.has('title_excessive_symbols') || codes.has('description_excessive_symbols')) tips.push('Remover símbolos, excesso de pontuação e letras separadas.');
    if (codes.has('title_leetspeak') || codes.has('description_leetspeak')) tips.push('Trocar escrita disfarçada por texto natural, sem números no lugar de letras.');
    if (codes.has('title_condition')) tips.push('Tirar condição do título e explicar estado da peça na descrição.');
    if (codes.has('title_short')) tips.push('Completar o título com peça, marca/modelo, motor ou ano apenas quando estiver claro.');
    if (codes.has('description_empty')) tips.push('Criar descrição com Produto, Aplicação, Código da peça, Estado e Observações.');
    if (codes.has('description_short')) tips.push('Enriquecer a descrição para reduzir dúvidas sobre compatibilidade e estado.');
    if (codes.has('description_glued_fields')) tips.push('Separar campos grudados, como marca, modelo, código e aplicação.');
    if (codes.has('description_unstructured')) tips.push('Quebrar o texto em blocos curtos e fáceis de conferir.');
    if (tips.length === 0) tips.push('Revisar clareza, compatibilidade e informações técnicas antes de salvar.');
    return tips;
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) return '-';
    const cleaned = value.replace('T', ' ').replace(/\.\d{3}.*$/, '').replace(/\+.*$/, '').trim();
    const [datePart, timePart] = cleaned.split(' ');
    const [year, month, day] = datePart.split('-');
    const formattedDate = year && month && day ? `${day}/${month}/${year}` : datePart;
    return `${formattedDate}${timePart ? ' ' + timePart.slice(0, 5) : ''}`;
  }

  private issueParam(): string | null {
    const map: Record<IssueFilter, string | null> = {
      all: null,
      bad_chars: 'bad_chars',
      description_empty: 'description_empty',
      description_short: 'description_short',
      few_pictures: 'few_pictures',
      few_attributes: 'few_attributes'
    };
    return map[this.issueFilter()];
  }

  private normalizeTitle(value: string): string {
    return value.replace(/\s+/g, ' ').trim();
  }

  private normalizeDescription(value: string): string {
    return value.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  }
}
